import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Design } from 'src/app/shared/models/design.model';
import { CurrentAppIdsDto } from '../models/current-app-ids-dto';
import { WellExplorerItem } from '../models/well-explorer.model';
import { SessionStorageKeys, StoreService } from './store.service';
import { UsersService } from './users-service';
import { WellExplorerService } from './well-explorer.service';

@Injectable({
  providedIn: 'root'
})
export class DesignContextService {
  private _hydratedDesignId: string | null = null;

  constructor(
    private _storeService: StoreService,
    private _usersService: UsersService,
    private _wellExplorerService: WellExplorerService
  ) {}

  public async syncDesignContext(
    appIds: CurrentAppIdsDto | null,
    routeDesignId: string | null,
    options: {
      isFirstSync: boolean;
      onNavigate?: (resolvedDesignId: string) => void;
    }
  ): Promise<Design | null> {
    if (!appIds) {
      return null;
    }

    if (options.isFirstSync) {
      if (routeDesignId && routeDesignId !== appIds.designId) {
        return this.setCurrentDesign(routeDesignId, {
          updateAppIds: true,
          onNavigate: (resolvedDesignId) => {
            if (resolvedDesignId) {
              options.onNavigate?.(resolvedDesignId);
            }
          }
        });
      }

      if (!routeDesignId && appIds.designId) {
        options.onNavigate?.(appIds.designId);
      }
    }

    return this.ensureDesignSession(appIds);
  }

  public async ensureDesignSession(appIds: CurrentAppIdsDto | null): Promise<Design | null> {
    const designId = appIds?.designId;
    if (!designId) {
      this._storeService.setSession(SessionStorageKeys.DESIGN, null);
      this._hydratedDesignId = null;
      return null;
    }

    const existingDesign = this._storeService.getSession<Design>(SessionStorageKeys.DESIGN);
    if (existingDesign?.id === designId && existingDesign?.name) {
      this._hydratedDesignId = designId;
      return existingDesign;
    }

    if (this._hydratedDesignId === designId) {
      return existingDesign ?? null;
    }

    try {
      const design = await this.resolveDesignById(designId);
      if (design) {
        this._storeService.setSession(SessionStorageKeys.DESIGN, design);
        this._hydratedDesignId = designId;
        return design;
      }
    } catch {
      // Keep existing behavior if design-name lookup fails.
    }

    return existingDesign ?? null;
  }

  public async setCurrentDesign(
    designId: string | null,
    options: {
      updateAppIds: boolean;
      fallbackName?: string;
      onNavigate?: (resolvedDesignId: string | null) => void;
    }
  ): Promise<Design | null> {
    const optimistic = designId && options.fallbackName
      ? new Design({ id: designId, name: options.fallbackName })
      : null;

    if (optimistic) {
      this._storeService.setSession(SessionStorageKeys.DESIGN, optimistic);
      this._hydratedDesignId = designId;
    }

    if (options.updateAppIds) {
      await lastValueFrom(this._usersService.updateCurrentAppIds({ designId }));
      const appIds = await lastValueFrom(this._usersService.getCurrentAppIds());
      this._storeService.setSession(SessionStorageKeys.APP_IDS, appIds);
      designId = appIds?.designId ?? designId;
      options.onNavigate?.(designId ?? null);
    }

    const finalDesign = designId
      ? await this.resolveDesignById(designId)
      : null;

    this._storeService.setSession(SessionStorageKeys.DESIGN, finalDesign);
    this._hydratedDesignId = finalDesign?.id ?? null;

    return finalDesign;
  }

  private async resolveDesignById(designId: string): Promise<Design | null> {
    const wellExplorer = await lastValueFrom(this._wellExplorerService.getWellExplorer());
    return this.findDesignById(wellExplorer, designId);
  }

  private findDesignById(root: WellExplorerItem | null, designId: string): Design | null {
    if (!root) {
      return null;
    }

    const stack: WellExplorerItem[] = [root];
    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) {
        continue;
      }

      if (node.level === 'designs' && node.id === designId) {
        return new Design({ id: node.id, name: node.name });
      }

      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return null;
  }
}